import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../components/services/apiService';
import { DirectoryTenant, authDirectoryBase, getUserDirectoryTree } from '../../components/services/userDirectoryService';

// The tree types live in the shared service (Tenant Management uses the same tree).
export type { DirectoryTenant, DirectoryTenantType } from '../../components/services/userDirectoryService';

export interface DirectoryUser {
  email: string;
  name: string;
}

export interface UserList {
  status: 'loading' | 'ready' | 'failed';
  users: DirectoryUser[];
}

export interface UserDirectory {
  /** The signed-in user's tenant with the tenants under it; null until loaded. */
  tree: DirectoryTenant | null;
  treeStatus: 'idle' | 'loading' | 'ready' | 'failed';
  /** Loads the tree (once; again after a failure). */
  loadTree: () => void;
  /** User lists by key: GLOBAL_ADMINS_KEY or a tenant id (lowercase). Missing = not asked for yet. */
  lists: Record<string, UserList>;
  /** Loads one list (once; again after a failure). */
  loadUsers: (key: string) => void;
}

export const GLOBAL_ADMINS_KEY = 'global-admins';

const AUTH_BASE = authDirectoryBase;

const toUsers = (rows: unknown): DirectoryUser[] => {
  const byEmail = new Map<string, DirectoryUser>();
  (Array.isArray(rows) ? rows : []).forEach((row: any) => {
    const email = String(row?.email ?? '').trim();
    if (email && !byEmail.has(email.toLowerCase())) byEmail.set(email.toLowerCase(), { email, name: String(row?.name ?? '').trim() });
  });
  return Array.from(byEmail.values());
};

/**
 * The Activity log's User filter list, from AuthService's user directory, loaded only as the user opens it:
 * - the tree (`GET {tenantId}/user-directory?includeUsers=false`, the signed-in user's own tenant) when the dropdown
 *   first opens: tenants labelled GlobalAdmin / Reseller / Tenant with user counts, no users;
 * - the Globaladmin users (`GET global-admin-users`) when that section is opened;
 * - one tenant's users (`GET {tenantId}/user-directory/users`) when that tenant is opened.
 * Each list is fetched once and kept. AuthService decides what the tree holds from the tenant and checks access.
 */
export const useUserDirectory = (ownTenantId: string | null): UserDirectory => {
  const [tree, setTree] = useState<DirectoryTenant | null>(null);
  const [treeStatus, setTreeStatus] = useState<UserDirectory['treeStatus']>('idle');
  const [lists, setLists] = useState<Record<string, UserList>>({});
  // Requests in flight or done, so a key is fetched once; a generation counter drops answers for a previous tenant.
  const requested = useRef(new Set<string>());
  const generation = useRef(0);

  useEffect(() => {
    generation.current += 1;
    requested.current = new Set();
    setTree(null);
    setTreeStatus('idle');
    setLists({});
  }, [ownTenantId]);

  const loadTree = useCallback(() => {
    if (!ownTenantId || requested.current.has('tree')) return;
    requested.current.add('tree');
    const gen = generation.current;
    setTreeStatus('loading');
    getUserDirectoryTree(ownTenantId)
      .then((directory) => {
        if (gen !== generation.current) return;
        setTree(directory);
        setTreeStatus('ready');
      })
      .catch(() => {
        // The shared API interceptor has already shown the error; the picker offers a retry and typing an email.
        if (gen !== generation.current) return;
        requested.current.delete('tree');
        setTreeStatus('failed');
      });
  }, [ownTenantId]);

  const loadUsers = useCallback((key: string) => {
    if (requested.current.has(key)) return;
    requested.current.add(key);
    const gen = generation.current;
    setLists((current) => ({ ...current, [key]: { status: 'loading', users: [] } }));
    const url =
      key === GLOBAL_ADMINS_KEY ? `${AUTH_BASE()}global-admin-users` : `${AUTH_BASE()}${encodeURIComponent(key)}/user-directory/users`;
    api
      .get(url)
      .then((response: any) => {
        if (gen !== generation.current) return;
        setLists((current) => ({ ...current, [key]: { status: 'ready', users: toUsers(response?.data?.data) } }));
      })
      .catch(() => {
        if (gen !== generation.current) return;
        requested.current.delete(key);
        setLists((current) => ({ ...current, [key]: { status: 'failed', users: [] } }));
      });
  }, []);

  return { tree, treeStatus, loadTree, lists, loadUsers };
};

export default useUserDirectory;
